import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateFaTotalDepreciationChangeMutation,
  useGetAssetRegistrationQuery,
  useGetCurrentDepreciationInfoLazyQuery,
  useGetDocumentDateValidationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  useUpdateFaTotalDepreciationChangeMutation,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'

interface TotalDepreciationFormData {
  asset_no: any
  asset_name: string
  start_date: Date | null
  model_no: string
  old_total_depreciation: number
  old_book_value: number
  change_date: Date
  adjustment_amount: number
  new_total_depreciation: number
  new_book_value: number
  description: string
  reason: string
}

interface CurrentDepreciationInfo {
  baseOriginalAmount: number
  openingAccumulatedDepreciation: number
  totalDecreaseOriginalValue: number
  totalIncreaseOriginalValue: number
  generatedDepreciation: number
  totalDepreciationAdjustment: number
  totalProvisionImpairment: number
  totalReversalImpairment: number
  currentOriginalValue: number
  currentTotalDepreciation: number
  currentBookValue: number
  quantity: number
  residualValue: number
  usefulLife: number
}

const safeParseNumber = (value: any): number => {
  if (value === undefined || value === null || value === '') return 0
  const parsed =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export const TotalDepreciationForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(null)
  const [
    currentDepreciationInfo,
    setCurrentDepreciationInfo,
  ] = useState<CurrentDepreciationInfo | null>(null)
  const [editDataLoaded, setEditDataLoaded] = useState(false)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let mode = formMode
  if (formMode === 'resubmit') {
    mode = 'edit'
  }

  const totalDepreciationSchema = yup.object().shape({
    asset_no: yup.mixed().required('Asset is required'),
    change_date: yup.date().required('Change Date is required'),
    description: yup.string().required('Description is required'),
    reason: yup.string().required('Reason is required'),
  })

  const { setValue, control, handleSubmit, watch, errors } = useForm<
    TotalDepreciationFormData
  >({
    defaultValues: {
      change_date: editData?.change_date
        ? new Date(editData.change_date)
        : new Date(),
      old_total_depreciation: editData?.old_total_depreciation || 0,
      old_book_value: editData?.old_book_value || 0,
      adjustment_amount: editData?.adjustment_amount || 0,
      new_total_depreciation: editData?.new_total_depreciation || 0,
      new_book_value: editData?.new_book_value || 0,
      description: editData?.description || '',
      reason: editData?.reason || '',
      asset_name: editData?.Asset?.name || '',
      start_date: editData?.Asset?.start_use_date
        ? new Date(editData.Asset.start_use_date)
        : null,
      model_no: editData?.Asset?.model_no || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(totalDepreciationSchema),
  })

  const watchAdjustAmount = watch('adjustment_amount')

  const {
    loading: assetsLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [
    getCurrentDepreciationInfo,
    { loading: depreciationInfoLoading },
  ] = useGetCurrentDepreciationInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getCurrentDepreciationInfo) {
        const info = data.getCurrentDepreciationInfo
        setCurrentDepreciationInfo(info as CurrentDepreciationInfo)
        setValue('old_total_depreciation', info.currentTotalDepreciation)
        setValue('old_book_value', info.currentBookValue)
        const isEditMode = formMode === 'edit' || formMode === 'resubmit'
        if (
          isEditMode &&
          editData?.adjustment_amount !== undefined &&
          !editDataLoaded
        ) {
          const existingAdjustment = safeParseNumber(editData.adjustment_amount)
          setValue('adjustment_amount', existingAdjustment)
          setValue(
            'new_total_depreciation',
            info.currentTotalDepreciation + existingAdjustment
          )
          setValue(
            'new_book_value',
            Math.max(0, info.currentBookValue - existingAdjustment)
          )
          setEditDataLoaded(true)
        } else if (!isEditMode) {
          setValue('adjustment_amount', 0)
          setValue('new_total_depreciation', info.currentTotalDepreciation)
          setValue('new_book_value', info.currentBookValue)
        }
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const assetsList = useMemo(() => {
    return getAssetRegistration || []
  }, [getAssetRegistration])

  const [
    createTotalDepreciationChange,
    { loading: createLoading },
  ] = useCreateFaTotalDepreciationChangeMutation({
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/total-depreciation`
      )
    },
  })

  const [
    updateTotalDepreciationChange,
    { loading: updateLoading },
  ] = useUpdateFaTotalDepreciationChangeMutation({
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/total-depreciation`
      )
    },
  })

  useEffect(() => {
    if (currentDepreciationInfo) {
      const adjustAmount = safeParseNumber(watchAdjustAmount)
      const currentTotalDepreciation =
        currentDepreciationInfo.currentTotalDepreciation
      const currentBookValue = currentDepreciationInfo.currentBookValue
      const newDepreciation = currentTotalDepreciation + adjustAmount
      const newBookValue = currentBookValue - adjustAmount

      setValue('new_total_depreciation', newDepreciation)
      setValue('new_book_value', newBookValue)
    }
  }, [watchAdjustAmount, currentDepreciationInfo, setValue])

  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.fa_asset_id &&
      assetsList.length > 0
    ) {
      const asset = assetsList.find(a => a.fa_asset_id === editData.fa_asset_id)
      if (asset) {
        setSelectedAsset(asset)
        setValue('asset_name', asset.name || '')
        setValue(
          'start_date',
          asset.start_use_date ? new Date(asset.start_use_date) : null
        )
        setValue('model_no', asset.model_no || '')

        if (editData.change_date) {
          setValue('change_date', new Date(editData.change_date))
        }
        if (editData.description) {
          setValue('description', editData.description)
        }
        if (editData.reason) {
          setValue('reason', editData.reason)
        }

        getCurrentDepreciationInfo({
          variables: {
            fa_asset_id: asset.fa_asset_id,
            exclude_change_id: editData.total_depreciation_change_id || null,
          },
        })
      }
    }
  }, [formMode, editData, assetsList])

  const handleAssetChange = (asset: any) => {
    if (asset) {
      setSelectedAsset(asset)
      setValue('asset_no', asset)
      setValue('asset_name', asset.name || '')
      setValue(
        'start_date',
        asset.start_use_date ? new Date(asset.start_use_date) : null
      )
      setValue('model_no', asset.model_no || '')

      getCurrentDepreciationInfo({
        variables: {
          fa_asset_id: asset.fa_asset_id,
          exclude_change_id: editData?.total_depreciation_change_id || null,
        },
      })
    } else {
      setSelectedAsset(null)
      setCurrentDepreciationInfo(null)
      setValue('asset_no', null)
      setValue('old_total_depreciation', 0)
      setValue('old_book_value', 0)
      setValue('adjustment_amount', 0)
      setValue('new_total_depreciation', 0)
      setValue('new_book_value', 0)
    }
  }

  const onSubmit = (
    data: TotalDepreciationFormData,
    approvalStatus: ApprovalStatus
  ) => {
    if (approvalStatus === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMsg(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrorDia(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMsg(
          'Cannot submit. The change date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrorDia(true)
        return
      }
    }

    const currentAsset = selectedAsset || editData?.Asset

    if (!currentAsset) {
      setErrMsg('Please select an asset')
      setErrorDia(true)
      return
    }

    if (!currentDepreciationInfo) {
      setErrMsg('Please wait for asset information to load')
      setErrorDia(true)
      return
    }

    const adjustmentAmount = safeParseNumber(data.adjustment_amount)

    if (adjustmentAmount === 0) {
      setErrMsg('Adjustment amount cannot be 0')
      setErrorDia(true)
      return
    }

    const currentTotalDepreciation =
      currentDepreciationInfo.currentTotalDepreciation
    const currentBookValue = currentDepreciationInfo.currentBookValue
    const newTotalDepreciation = currentTotalDepreciation + adjustmentAmount
    const newBookValue = currentBookValue - adjustmentAmount

    if (newBookValue < 0) {
      setErrMsg(
        `Adjustment would result in negative book value. ` +
          `Current book value: ${formatCurrency(
            currentBookValue
          )}, Adjustment: ${formatCurrency(adjustmentAmount)}`
      )
      setErrorDia(true)
      return
    }

    const input = {
      total_depreciation_change_id:
        editData?.total_depreciation_change_id || undefined,
      fa_asset_id: currentAsset.fa_asset_id,
      company_id: CompanyID,
      old_total_depreciation: currentTotalDepreciation,
      old_book_value: currentBookValue,
      new_total_depreciation: newTotalDepreciation,
      new_book_value: newBookValue,
      adjustment_amount: adjustmentAmount,
      change_date: data.change_date,
      description: data.description,
      reason: data.reason,
      ApprovalStatus: approvalStatus,
    }

    if (formMode === 'add') {
      createTotalDepreciationChange({ variables: { input } })
    } else {
      updateTotalDepreciationChange({ variables: { input } })
    }
  }

  const watchChangeDate =
    watch('change_date') || editData?.change_date || new Date()
  const changeDateStr = (() => {
    const d = new Date(watchChangeDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? changeDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        changeDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const changeDate = new Date(watchChangeDate)
  const isDocDateInvalid =
    changeDate.getTime() < minValidDate.getTime() ||
    changeDate.getTime() > maxValidDate.getTime()

  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (formMode === 'add' || formMode === 'edit' || formMode === 'resubmit') {
    footerOptions = [draftFooterOption, submitFooterOption]
  } else {
    footerOptions = []
  }

  return (
    <>
      {(assetsLoading ||
        createLoading ||
        updateLoading ||
        depreciationInfoLoading) && <Loading />}

      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/total-depreciation`
          )
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Total Depreciation', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents
          section={{
            header: { title: 'Asset Information' },
          }}
        >
          <Controller
            render={({ value, onChange }) => {
              const defVal = editData?.Asset || null

              return (
                <Autocomplete
                  options={
                    assetsList?.sort((a, b) => {
                      return a?.DocNo?.localeCompare(b?.DocNo)
                    }) || []
                  }
                  getOptionLabel={option =>
                    `${option?.DocNo} - ${option?.name}`
                  }
                  fullWidth
                  onChange={(event, value: any) => handleAssetChange(value)}
                  defaultValue={defVal}
                  disabled={mode === 'approve-reject'}
                  renderOption={props => (
                    <div>
                      <div>
                        <span className="xsTitle">{props?.DocNo}</span>
                        <span className="desc" style={{ marginLeft: '8px' }}>
                          {props?.name}
                        </span>
                      </div>
                    </div>
                  )}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Asset"
                      style={{ width: '100%' }}
                      required
                      margin="normal"
                      helperText={errors?.asset_no?.message}
                      error={errors?.asset_no ? true : false}
                    />
                  )}
                />
              )
            }}
            name="asset_no"
            control={control}
            margin="normal"
            defaultValue={editData?.Asset || null}
          />

          <Controller
            as={TextField}
            name="asset_name"
            label="Asset Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            defaultValue={editData?.Asset?.name || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Asset Start Date"
                  format="dd/MM/yyyy"
                  readOnly
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    style: { display: 'none' },
                  }}
                  className="left"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                />
              )}
              name="start_date"
              control={control}
              defaultValue={
                editData?.Asset?.start_use_date
                  ? new Date(editData.Asset.start_use_date)
                  : new Date()
              }
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            name="model_no"
            label="Model No"
            autoComplete="off"
            control={control}
            className="right"
            margin="dense"
            defaultValue={editData?.Asset?.model_no || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Total Depreciation"
                fullWidth
                className="left"
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                // helperText={
                //   currentDepreciationInfo
                //     ? `Opening: ${formatCurrency(
                //         currentDepreciationInfo.openingAccumulatedDepreciation
                //       )} | +Generated: ${formatCurrency(
                //         currentDepreciationInfo.generatedDepreciation
                //       )} | +Adj: ${formatCurrency(
                //         currentDepreciationInfo.totalDepreciationAdjustment
                //       )}`
                //     : ''
                // }
                helperText={
                  currentDepreciationInfo
                    ? `+Generated: ${formatCurrency(
                        currentDepreciationInfo.generatedDepreciation
                      )} | +Adj: ${formatCurrency(
                        currentDepreciationInfo.totalDepreciationAdjustment
                      )}`
                    : ''
                }
              />
            )}
            name="old_total_depreciation"
            control={control}
            defaultValue={editData?.old_total_depreciation || 0}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Book Value"
                fullWidth
                className="right"
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                helperText={
                  currentDepreciationInfo
                    ? `Original: ${formatCurrency(
                        currentDepreciationInfo.currentOriginalValue
                      )} - Dep: ${formatCurrency(
                        currentDepreciationInfo.currentTotalDepreciation
                      )}`
                    : ''
                }
              />
            )}
            name="old_book_value"
            control={control}
            defaultValue={editData?.old_book_value || 0}
          />
        </CardContents>

        <CardContents
          section={{
            header: { title: 'Acc. Depreciation Adjustment' },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Date"
                  format="dd/MM/yyyy"
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  required
                  disabled={mode === 'approve-reject'}
                />
              )}
              name="change_date"
              control={control}
              defaultValue={
                editData?.change_date
                  ? new Date(editData.change_date)
                  : new Date()
              }
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                onValueChange={({ floatValue }) => onChange(floatValue || 0)}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                allowNegative={true}
                label="Adjust Amount"
                fullWidth
                className="left"
                margin="dense"
                required
                disabled={mode === 'approve-reject'}
                helperText="Positive = Increase depreciation, Negative = Decrease depreciation"
              />
            )}
            name="adjustment_amount"
            control={control}
            defaultValue={editData?.adjustment_amount || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                allowNegative={true}
                label="Net Value (New Book Value)"
                fullWidth
                className="right"
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="new_book_value"
            control={control}
            defaultValue={editData?.new_book_value || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Acc. Depreciation Amount (New Total Depreciation)"
                fullWidth
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="new_total_depreciation"
            control={control}
            defaultValue={editData?.new_total_depreciation || 0}
          />

          <VoiceTextField
            mounted={true}
            label="Description"
            name="description"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.description || '',
              helperText: errors?.description?.message,
              error: errors?.description ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="reason"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.reason || '',
              helperText: errors?.reason?.message,
              error: errors?.reason ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/total-depreciation`
          )
        }}
      />
    </>
  )
}
